import { useEffect, useRef, useState } from 'react'
import { Film, Play, Trash2, Upload } from 'lucide-react'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import {
  completeOfflineRecording, deleteCoverageRecording, listCoverageRecordings, reserveOfflineRecording,
} from '../api'
import { CoverageReplayPlayer, fmtBytes, fmtClock } from '../players'
import { recordingSourceLabel, recordingStateLabel } from '../vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse, CoverageRecordingResponse } from '@/shared/api/types'

/** The server refuses anything else before it mints an upload credential. Mirror it here. */
const ALLOWED = ['video/mp4', 'video/webm', 'video/quicktime', 'audio/mp4', 'audio/webm']
/** The storage purpose policy's ceiling for COVERAGE_RECORDING. Checked here too, so a
 *  two-hour file is refused before it costs somebody their data bundle. */
const MAX_BYTES = 8 * 1024 * 1024 * 1024

export function RecordingsPanel({
  event, onChanged,
}: { event: CoverageEventResponse; onChanged: () => void }) {
  const canWatch = useCan('coverage:watch')
  const canDelete = useCan('coverage:delete')
  const canBroadcast = useCan('coverage:broadcast')
  const [tick, setTick] = useState(0)
  const [playing, setPlaying] = useState<CoverageRecordingResponse | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, error: loadError, loading } = useLoad(
    () => listCoverageRecordings(event.id), [event.id, tick],
  )
  const rows = data ?? []

  return (
    <div className="space-y-3">
      <ErrorStrip message={loadError ?? error} />

      <DrawerSection
        title="Recordings"
        right={canBroadcast ? (
          <button type="button" className="erp-btn h-7" onClick={() => setUploading(true)}>
            <Upload className="h-3.5 w-3.5" /> Upload from the field
          </button>
        ) : undefined}
      >
        {loading && rows.length === 0 ? <LoadingBlock label="Loading recordings…" />
          : rows.length === 0 ? (
            <Note tone="info">
              Nothing recorded yet. A camera taken live here is recorded by the media server
              as it goes out — whether or not anybody is watching.
            </Note>
          ) : (
            <ul className="space-y-1.5">
              {rows.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
                  <Film className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-erp-sm">
                      {recordingSourceLabel(r.sourceCode)}
                      {r.capturedByName ? ` · ${r.capturedByName}` : ''}
                    </div>
                    <div className="truncate text-erp-xs text-muted-foreground">
                      {recordingStateLabel(r.stateCode)}
                      {r.durationSec != null && ` · ${fmtClock(r.durationSec)}`}
                      {r.sizeBytes != null && ` · ${fmtBytes(r.sizeBytes)}`}
                      {r.endedAt && ` · ${new Date(r.endedAt).toLocaleString()}`}
                    </div>
                    {r.error && <div className="text-erp-xs text-red-600">{r.error}</div>}
                  </div>
                  {r.stateCode === 'READY' && canWatch && (
                    <button type="button" className="erp-btn h-7" onClick={() => setPlaying(r)}>
                      <Play className="h-3.5 w-3.5" /> Play
                    </button>
                  )}
                  {canDelete && r.stateCode !== 'RECORDING' && (
                    <DeleteRecording
                      recording={r}
                      onDone={() => { setTick((t) => t + 1); onChanged() }}
                      onError={setError}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
      </DrawerSection>

      {playing && (
        <Modal open onClose={() => setPlaying(null)} title="Recording" width="lg">
          <CoverageReplayPlayer
            recordingId={playing.id}
            stateCode={playing.stateCode}
            className="aspect-video w-full"
          />
        </Modal>
      )}

      {uploading && (
        <OfflineUploadDialog
          event={event}
          onClose={() => setUploading(false)}
          onDone={() => { setUploading(false); setTick((t) => t + 1); onChanged() }}
        />
      )}
    </div>
  )
}

function DeleteRecording({
  recording, onDone, onError,
}: {
  recording: CoverageRecordingResponse
  onDone: () => void
  onError: (m: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <>
      <button type="button" className="erp-btn h-7" aria-label="Delete recording"
        onClick={() => setOpen(true)}>
        <Trash2 className="h-3.5 w-3.5" />
      </button>
      {open && (
        <Modal open onClose={() => setOpen(false)} title="Delete this recording">
          <div className="space-y-3">
            <p className="text-erp-sm text-muted-foreground">
              The file is deleted from storage as well as the row. This cannot be undone, and
              it is recorded in the audit log as a security event.
            </p>
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium">Reason</span>
              <input className="erp-input w-full" value={reason}
                onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="erp-btn" onClick={() => setOpen(false)}>Keep it</button>
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!reason.trim() || busy}
                onClick={async () => {
                  setBusy(true); onError(null)
                  try { await deleteCoverageRecording(recording.id, reason.trim()); setOpen(false); onDone() }
                  catch (e) { onError(explain(e)) } finally { setBusy(false) }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

/**
 * The field-network path: the phone recorded locally because it could not reach
 * the media server, and the file arrives later.
 *
 * The content type AND the size are checked here as well as server-side, and the
 * server's check happens before it mints the presigned URL — handing out a write
 * credential for a file the policy will reject after the upload has already cost
 * somebody their data bundle is the failure this ordering avoids.
 */
function OfflineUploadDialog({
  event, onClose, onDone,
}: { event: CoverageEventResponse; onClose: () => void; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [feedId, setFeedId] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [progress, setProgress] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const liveRef = useRef(true)
  useEffect(() => () => { liveRef.current = false; abortRef.current?.abort() }, [])

  const typeOk = !file || ALLOWED.includes(file.type)
  const sizeOk = !file || file.size <= MAX_BYTES

  return (
    <Modal
      open
      // Dismissing mid-PUT unmounted the component while three setState calls
      // were still pending in the `finally`, and left the reserved row PENDING
      // forever with nothing able to complete it.
      onClose={busy ? () => {} : onClose}
      title="Upload a recording from the field"
    >
      <div className="space-y-3">
        <p className="text-erp-sm text-muted-foreground">
          For a camera that could not reach the media server. The file goes straight to
          storage — it does not pass through the API.
        </p>
        <ErrorStrip message={error} />

        <label className="block space-y-1">
          <span className="text-erp-xs font-medium">File</span>
          <input
            type="file"
            className="erp-input w-full"
            accept={ALLOWED.join(',')}
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null) }}
          />
        </label>
        {file && !typeOk && (
          <Note tone="bad">
            {file.type || 'That file type'} is not accepted. Video: MP4, WebM or QuickTime.
            Audio: M4A or WebM.
          </Note>
        )}
        {file && typeOk && !sizeOk && (
          <Note tone="bad">
            That file is larger than the {Math.round(MAX_BYTES / 1024 ** 3)} GB the storage
            policy allows. Split it, or ask for the limit to be raised.
          </Note>
        )}

        {event.feeds.length > 0 && (
          <label className="block space-y-1">
            <span className="text-erp-xs font-medium">Which camera</span>
            <select className="erp-input w-full" value={feedId}
              onChange={(e) => setFeedId(e.target.value)}>
              <option value="">Not a particular camera</option>
              {event.feeds.map((f) => <option key={f.id} value={f.id}>{f.title}</option>)}
            </select>
          </label>
        )}

        {progress && <p className="text-erp-xs text-muted-foreground">{progress}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="erp-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!file || !typeOk || !sizeOk || busy}
            onClick={async () => {
              if (!file) return
              setBusy(true); setError(null)
              try {
                setProgress('Reserving space…')
                const reserved = await reserveOfflineRecording(event.id, {
                  feedId: feedId || null,
                  filename: file.name,
                  contentType: file.type,
                  sizeBytes: file.size,
                  capturedAt: new Date(file.lastModified).toISOString(),
                })
                if (!reserved.uploadUrl) {
                  throw new Error('This build has no media store, so there is nowhere to '
                    + 'upload to. The reservation was not made.')
                }
                setProgress('Uploading…')
                const ac = new AbortController()
                abortRef.current = ac
                const put = await fetch(reserved.uploadUrl, {
                  method: 'PUT',
                  headers: { 'Content-Type': file.type },
                  body: file,
                  signal: ac.signal,
                })
                if (!put.ok) throw new Error(`The upload was refused (${put.status}).`)
                if (!liveRef.current) return
                setProgress('Confirming…')
                await completeOfflineRecording(reserved.recordingId)
                if (!liveRef.current) return
                onDone()
              } catch (e) {
                if (liveRef.current) setError(explain(e))
              } finally {
                abortRef.current = null
                if (liveRef.current) { setBusy(false); setProgress(null) }
              }
            }}
          >
            Upload
          </button>
        </div>
      </div>
    </Modal>
  )
}
